import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbInfoCircle, TbX } from "solid-icons/tb";
import { type Component, createEffect, createSignal, Match, on, Show, Switch } from "solid-js";
import { useI18n } from "~/i18n/context";
import { translateWithJSX } from "~/i18n/utils";

interface DeleteChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  chatTitle: string;
  isShared: boolean;
  projectName?: string;
  errorMessage?: string | null;
  isDeleting?: boolean;
  isStreaming?: boolean;
}

export const DeleteChatModal: Component<DeleteChatModalProps> = (props) => {
  const { t } = useI18n();

  // Latch the streaming flag for the modal's open lifetime so the layout doesn't
  // flip to the confirm view during the exit animation when isStreaming flips false.
  const [streamingMode, setStreamingMode] = createSignal(false);
  createEffect(
    on(
      () => props.isOpen,
      (isOpen, prevOpen) => {
        if (isOpen && !prevOpen) setStreamingMode(!!props.isStreaming);
      }
    )
  );

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-90 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-90 flex items-center justify-center">
              <Dialog.Content class="relative max-h-[80vh] w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>

                <Show
                  when={streamingMode()}
                  fallback={
                    <>
                      <div class="mb-6">
                        <h3 class="mb-4 text-lg font-medium">
                          {t("modal_delete_chat_title", { chatTitle: props.chatTitle })}
                        </h3>
                        <div
                          role={props.errorMessage ? "alert" : undefined}
                          class="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/20"
                        >
                          <div class="flex items-center gap-2">
                            <span class="text-lg">🛑</span>
                            <p class="text-sm text-red-800 dark:text-red-200">
                              <Switch fallback={<>{t("modal_delete_chat_private")}</>}>
                                <Match when={props.errorMessage}>{props.errorMessage}</Match>
                                <Match when={props.isShared}>
                                  {translateWithJSX(t, "modal_delete_chat_shared", {
                                    allUsers: <span class="font-bold">{t("modal_delete_chat_all_users")}</span>,
                                    projectName: props.projectName || t("common.this_project"),
                                  })}
                                </Match>
                              </Switch>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center justify-end gap-3">
                        <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                          {t("common.cancel")}
                        </StButton>
                        <StButton
                          onClick={props.onConfirm}
                          loading={props.isDeleting}
                          disabled={props.isDeleting}
                          class="bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                        >
                          {t("modal_delete_chat_confirm")}
                        </StButton>
                      </div>
                    </>
                  }
                >
                  {/* Informational state: chat is still streaming, can't be deleted yet. */}
                  <div class="mb-6">
                    <h3 class="mb-4 text-lg font-medium">
                      {t("modal_delete_chat_title", { chatTitle: props.chatTitle })}
                    </h3>
                    <output class="block rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20">
                      <div class="flex items-start gap-2">
                        <TbInfoCircle class="mt-0.5 size-5 shrink-0 text-amber-700 dark:text-amber-300" />
                        <p class="text-sm text-amber-900 dark:text-amber-100">{t("chats.delete.streaming_active")}</p>
                      </div>
                    </output>
                  </div>
                  <div class="flex items-center justify-end gap-3">
                    <StButton onClick={props.onClose} class="bg-brand px-4 py-2 text-white hover:bg-brand/90">
                      {t("modal_delete_chat_later")}
                    </StButton>
                  </div>
                </Show>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
